import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { FaTrash } from 'react-icons/fa';

import { FieldType, OPTIONAL_UPDATE_FIELDS } from '../constants/csvFields';
import { colors } from 'constants/colors';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemLabel } from 'components/TotemLabel';
import { CsvDataRow, TotemCsvInputButton } from 'components/TotemCsvInputButton';

import { formatSiteCsvData } from './helpers/formatSiteCsvData';
import { checkSiteCsvImport } from './helpers/checkSiteCsvImport';
import { useUpdateSitesFromImportMutation, SiteUpdateCsvImport } from 'data/__generated__';

export const CsvImport = () => {
    const [csvData, setCsvData] = useState<Array<CsvDataRow>>([]);
    const [updateSites, { loading: loadingCsv }] = useUpdateSitesFromImportMutation();

    const handleCSVImport = async () => {
        try {
            // Check CSV validity first
            const checkResult = checkSiteCsvImport(csvData);
            if (!checkResult.success) {
                toast.error(checkResult.error);
                return;
            }

            const formattedData = formatSiteCsvData({ csvData });

            const { data } = await updateSites({
                variables: {
                    sitesToUpdate: formattedData as SiteUpdateCsvImport[],
                },
            });

            if (data?.updateSitesFromImport.success) {
                toast.success('Les sites ont bien été mis à jour !');
                setCsvData([]);
            } else if (data?.updateSitesFromImport.error) {
                toast.error(data.updateSitesFromImport.error, { autoClose: false });
            } else {
                throw new Error("Une erreur inconnue s'est produite");
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Une erreur inconnue s'est produite");
        }
    };

    return (
        <Content>
            <SelectionSection>
                <SelectionContainer>
                    <TotemLabel>Import CSV pour modification des sites</TotemLabel>
                    {csvData.length ? (
                        <ButtonContainer>
                            <TotemPrimaryButton onClick={() => setCsvData([])}>
                                <FaTrash data-test="trash-icon" size={12} color={colors.pureWhite} />
                            </TotemPrimaryButton>
                            <TotemPrimaryLoadingButton
                                $minWidth="105px"
                                onClick={handleCSVImport}
                                loading={loadingCsv}
                                useIcon={false}
                                label="Import CSV"
                            />
                        </ButtonContainer>
                    ) : (
                        <TotemCsvInputButton onCsvDataUpload={setCsvData} />
                    )}
                    <Warning>
                        Le champ '_id' est obligatoire pour chaque ligne du CSV. Les autres champs sont optionnels. Une
                        cellule vide ne modifiera pas la valeur existante.
                    </Warning>
                    <ColumnContainerExample>
                        <TotemLabel>Exemple d'utilisation:</TotemLabel>
                        <p>deliveryDays.[sunday/monday/...] (true/false)</p>
                        <p>deliveryDays.monday true</p>
                        <p>Jour de livraison lundi: true</p>
                    </ColumnContainerExample>
                </SelectionContainer>
                <ColumnContainer>
                    <TotemLabel>Champ requis pour la modification:</TotemLabel>
                    <div>
                        • <b>_id</b> (identifiant du site à modifier)
                    </div>
                    <div style={{ height: '1rem' }} />
                    <TotemLabel>Champs optionnels pour la Modification:</TotemLabel>
                    {[...OPTIONAL_UPDATE_FIELDS].map((field: FieldType, index) => {
                        const { name, displayInfo = '', isHidden } = field;
                        if (name === '_id' || isHidden) return null;
                        return (
                            <div key={index}>
                                • <b>{name}</b> {displayInfo}
                            </div>
                        );
                    })}
                </ColumnContainer>
            </SelectionSection>
        </Content>
    );
};

const ButtonContainer = styled.div`
    display: flex;
    width: max-content;
    gap: 5px;
`;

export const ColumnContainerExample = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
    p {
        margin: 0;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: scroll;
`;

const SelectionSection = styled.div`
    display: flex;
    height: 100%;
    overflow: hidden;
`;

const SelectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 20%;
`;

const ColumnContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    ::-webkit-scrollbar {
        width: 5px;
        height: 0;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: ${({ theme }) => theme.borderRadius};
        background-color: ${({ theme }) => theme.ctaPrimaryColor};
    }
    margin-left: 10px;
    font-size: 14px;
    width: 50%;
`;

const Warning = styled.div`
    display: flex;
    margin-top: 20px;
`;
